import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import RefreshIcon      from '@mui/icons-material/Refresh';
import SearchIcon       from '@mui/icons-material/Search';
import SyncIcon         from '@mui/icons-material/Sync';
import VpnKeyIcon       from '@mui/icons-material/VpnKey';
import WarningIcon      from '@mui/icons-material/Warning';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import * as imLeadsApi from '../services/imLeadsApi';

// ─── Constants ────────────────────────────────────────────────────────────────

const QUERY_TYPE_CONFIG = {
  W:   { label: 'Direct Enquiry', color: 'info' },
  B:   { label: 'Buy Lead',       color: 'success' },
  P:   { label: 'PNS Call',       color: 'warning' },
  BIZ: { label: 'Catalog View',   color: 'secondary' },
  WA:  { label: 'WhatsApp',       color: 'primary' },
};

function QueryTypeChip({ type }) {
  const cfg = QUERY_TYPE_CONFIG[type] || { label: type || 'Unknown', color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// ─── Lead Detail Dialog ───────────────────────────────────────────────────────

function LeadDetailDialog({ lead, onClose }) {
  if (!lead) return null;
  const rows = [
    ['Enquiry ID',      lead.UNIQUE_QUERY_ID],
    ['Type',            <QueryTypeChip type={lead.QUERY_TYPE} />],
    ['Time',            lead.QUERY_TIME],
    ['Name',            lead.SENDER_NAME],
    ['Mobile',          lead.SENDER_MOBILE],
    ['Alt Mobile',      lead.SENDER_MOBILE_ALT],
    ['Phone',           lead.SENDER_PHONE],
    ['Email',           lead.SENDER_EMAIL],
    ['Alt Email',       lead.SENDER_EMAIL_ALT],
    ['Company',         lead.SENDER_COMPANY],
    ['Address',         lead.SENDER_ADDRESS],
    ['City',            lead.SENDER_CITY],
    ['State',           lead.SENDER_STATE],
    ['Pincode',         lead.SENDER_PINCODE],
    ['Country',         lead.SENDER_COUNTRY_ISO],
    ['Product',         lead.QUERY_PRODUCT_NAME],
    ['Category',        lead.QUERY_MCAT_NAME],
    ['Subject',         lead.SUBJECT],
    ['Message',         lead.QUERY_MESSAGE],
    ['Call Duration',   lead.CALL_DURATION],
    ['Receiver Mobile', lead.RECEIVER_MOBILE],
  ].filter(([, v]) => v != null && v !== '');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Lead Details
        <Typography variant="body2" color="text.secondary">{lead.UNIQUE_QUERY_ID}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1.25, columnGap: 2 }}>
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, pt: 0.25 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Leads Tab ────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { search: '', queryType: '', fromDate: '', toDate: '', city: '', state: '' };
const PAGE_LIMIT = 20;

function LeadsTab() {
  const [leads,   setLeads]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await imLeadsApi.listLeads(filters, page, PAGE_LIMIT);
      setLeads(result.data  || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleFilter = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
    setPage(1);
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
          <TextField
            label="Search" size="small" sx={{ minWidth: 180 }} value={filters.search}
            onChange={handleFilter('search')}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={filters.queryType} onChange={handleFilter('queryType')}>
              <MenuItem value="">All Types</MenuItem>
              {Object.entries(QUERY_TYPE_CONFIG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="From Date" size="small" type="date" sx={{ minWidth: 140 }}
            value={filters.fromDate} onChange={handleFilter('fromDate')} InputLabelProps={{ shrink: true }} />
          <TextField label="To Date" size="small" type="date" sx={{ minWidth: 140 }}
            value={filters.toDate} onChange={handleFilter('toDate')} InputLabelProps={{ shrink: true }} />
          <TextField label="City" size="small" sx={{ minWidth: 110 }}
            value={filters.city} onChange={handleFilter('city')} />
          <TextField label="State" size="small" sx={{ minWidth: 110 }}
            value={filters.state} onChange={handleFilter('state')} />
          <Button size="small" variant="outlined" onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}>
            Clear
          </Button>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchLeads}>
            Refresh
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Product / Subject</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No leads found
                  </TableCell>
                </TableRow>
              ) : leads.map((lead, idx) => (
                <TableRow key={lead._id} hover>
                  <TableCell>{(page - 1) * PAGE_LIMIT + idx + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {lead.QUERY_TIME
                      ? (dayjs(lead.QUERY_TIME).isValid()
                          ? dayjs(lead.QUERY_TIME).format('DD MMM YYYY')
                          : lead.QUERY_TIME.slice(0, 11))
                      : '—'}
                  </TableCell>
                  <TableCell>{lead.SENDER_NAME || '—'}</TableCell>
                  <TableCell><QueryTypeChip type={lead.QUERY_TYPE} /></TableCell>
                  <TableCell>{lead.SENDER_MOBILE || lead.SENDER_PHONE || '—'}</TableCell>
                  <TableCell>{lead.SENDER_COMPANY || '—'}</TableCell>
                  <TableCell>
                    {[lead.SENDER_CITY, lead.SENDER_STATE].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lead.QUERY_PRODUCT_NAME || lead.SUBJECT || '—'}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setSelected(lead)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">{total.toLocaleString()} total leads</Typography>
          <Pagination
            count={Math.ceil(total / PAGE_LIMIT)} page={page}
            onChange={(_, v) => setPage(v)} size="small" color="primary"
          />
        </Box>
      </Paper>

      <LeadDetailDialog lead={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}

// ─── Sync History Tab ─────────────────────────────────────────────────────────

function SyncHistoryTab() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await imLeadsApi.getSyncLogs();
      setLogs(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchLogs}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started At</TableCell>
                <TableCell>Window</TableCell>
                <TableCell align="right">Fetched</TableCell>
                <TableCell align="right">New</TableCell>
                <TableCell align="right">Updated</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No sync history yet
                  </TableCell>
                </TableRow>
              ) : logs.map((log) => (
                <TableRow key={log._id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {dayjs(log.createdAt).format('DD MMM HH:mm')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {log.startDate} → {log.endDate}
                  </TableCell>
                  <TableCell align="right">{log.totalFetched}</TableCell>
                  <TableCell align="right">{log.totalInserted}</TableCell>
                  <TableCell align="right">{log.totalUpdated}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {log.durationMs ? `${(log.durationMs / 1000).toFixed(1)}s` : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.status}
                      size="small"
                      color={log.status === 'success' ? 'success' : log.status === 'partial' ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'error.main' }}>
                    <Tooltip title={log.error || ''} placement="top" disableHoverListener={!log.error}>
                      <span>{log.error || '—'}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab({ onSyncNow, syncLoading, syncError, syncSuccess }) {
  const { isAdmin } = useAuth();
  const [status,          setStatus]          = useState(null);
  const [backfillState,   setBackfillState]   = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError,   setBackfillError]   = useState(null);
  const [backfillSuccess, setBackfillSuccess] = useState(false);
  const [testKeyLoading,  setTestKeyLoading]  = useState(false);
  const [testKeyResult,   setTestKeyResult]   = useState(null);
  const [testKeyError,    setTestKeyError]    = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await imLeadsApi.getStatus();
      setStatus(result.data);
    } catch (err) {
      console.error('[IM Settings] status fetch failed:', err.message);
    }
  }, []);

  const fetchBackfill = useCallback(async () => {
    try {
      const result = await imLeadsApi.getBackfillStatus();
      setBackfillState(result.data);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchBackfill();
    pollRef.current = setInterval(() => { fetchStatus(); fetchBackfill(); }, 30_000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus, fetchBackfill]);

  const handleTestKey = async () => {
    setTestKeyLoading(true);
    setTestKeyResult(null);
    setTestKeyError(null);
    try {
      const result = await imLeadsApi.testKey();
      setTestKeyResult(result);
      fetchStatus(); // refresh status card
    } catch (err) {
      setTestKeyError(err.message);
    } finally {
      setTestKeyLoading(false);
    }
  };

  const handleBackfill = async () => {
    setBackfillLoading(true);
    setBackfillError(null);
    setBackfillSuccess(false);
    try {
      await imLeadsApi.startBackfill();
      setBackfillSuccess(true);
      await fetchBackfill();
    } catch (err) {
      setBackfillError(err.message);
    } finally {
      setBackfillLoading(false);
    }
  };

  const fmt = (d) => d ? dayjs(d).format('DD MMM YYYY HH:mm') : '—';

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Integration Status</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            {!status ? (
              <CircularProgress size={18} />
            ) : status.keyExpired ? (
              <WarningIcon color="error" fontSize="small" />
            ) : (
              <CheckCircleIcon color="success" fontSize="small" />
            )}
            <Typography variant="body2">
              API Key: <strong>{status?.keyMasked || '••••••••'}</strong>
              {status?.keyExpired && <Chip label="Expired" size="small" color="error" sx={{ ml: 1 }} />}
            </Typography>
          </Box>

          {status?.keyExpired && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '0.8125rem' }}>
              The API key has expired. Generate a new one at{' '}
              <strong>seller.indiamart.com → LeadManager → CRM API</strong>, update{' '}
              <code>INDIA_MART_API_KEY</code> in the server <code>.env</code>, then restart the server.
            </Alert>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Last Sync</Typography>
              <Typography variant="body2">{fmt(status?.lastSyncAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Next Sync (estimated)</Typography>
              <Typography variant="body2">{fmt(status?.nextSyncAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Sync Interval</Typography>
              <Typography variant="body2">Every 10 minutes</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Consecutive Failures</Typography>
              <Typography variant="body2" color={status?.consecutiveFailures > 0 ? 'error.main' : 'text.primary'}>
                {status?.consecutiveFailures ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Leads in DB</Typography>
              <Typography variant="body2">{status?.totalLeads?.toLocaleString() ?? '—'}</Typography>
            </Box>
          </Box>

          {status?.lastError && (
            <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
              Last error: {status.lastError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained" size="small"
              startIcon={syncLoading ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              onClick={onSyncNow} disabled={syncLoading || status?.keyExpired}
            >
              Sync Now
            </Button>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchStatus(); fetchBackfill(); }}>
              Refresh
            </Button>
            {isAdmin && (
              <Tooltip title="Send a live test request to IndiaMART to verify the key is working">
                <span>
                  <Button
                    size="small" variant="outlined" color="secondary"
                    startIcon={testKeyLoading ? <CircularProgress size={14} color="inherit" /> : <VpnKeyIcon />}
                    onClick={handleTestKey} disabled={testKeyLoading}
                  >
                    Test Key
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {syncError     && <Alert severity="error"   sx={{ mt: 1 }}>{syncError}</Alert>}
          {syncSuccess   && <Alert severity="success" sx={{ mt: 1 }}>Sync triggered — results will appear in Sync History shortly.</Alert>}
          {testKeyError  && <Alert severity="error"   sx={{ mt: 1 }}>Test failed: {testKeyError}</Alert>}
          {testKeyResult && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Key is valid — {testKeyResult.totalLeads} lead(s) in test window [{testKeyResult.window?.startStr} → {testKeyResult.window?.endStr}]
            </Alert>
          )}
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Historical Backfill</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Imports all leads from the configured historical window (Jan–Dec 2025 by default).
              Runs in the background — ~4–5 hours total. Fully resumable if the server restarts.
            </Typography>

            {backfillState && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={
                    backfillState.completed
                      ? 'Completed'
                      : backfillState.lastProcessedStartDate
                      ? 'In Progress / Resumable'
                      : 'Not started'
                  }
                  color={backfillState.completed ? 'success' : backfillState.lastProcessedStartDate ? 'warning' : 'default'}
                  size="small"
                  sx={{ mb: 0.75 }}
                />
                {backfillState.lastProcessedStartDate && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Last chunk: {backfillState.lastProcessedStartDate} → {backfillState.lastProcessedEndDate}
                  </Typography>
                )}
              </Box>
            )}

            {backfillError   && <Alert severity="error"   sx={{ mb: 1 }}>{backfillError}</Alert>}
            {backfillSuccess && <Alert severity="success" sx={{ mb: 1 }}>Backfill started in the background.</Alert>}

            <Button
              variant="outlined" color="warning" size="small"
              onClick={handleBackfill} disabled={backfillLoading}
              startIcon={backfillLoading ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {backfillState?.completed ? 'Re-run Backfill' : 'Start Backfill'}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ─── Tab Panel Helper ─────────────────────────────────────────────────────────

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IndiaMART() {
  const [tab,         setTab]         = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError,   setSyncError]   = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(false);

  const handleSyncNow = async () => {
    setSyncLoading(true);
    setSyncError(null);
    setSyncSuccess(false);
    try {
      await imLeadsApi.syncNow();
      setSyncSuccess(true);
      setTimeout(() => setSyncSuccess(false), 5_000);
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">IndiaMART Leads</Typography>
        <Button
          variant="contained" size="small"
          startIcon={syncLoading ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
          onClick={handleSyncNow} disabled={syncLoading}
        >
          Sync Now
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box component="nav" sx={{ display: 'flex', gap: 0 }}>
          {['Leads', 'Sync History', 'Settings'].map((label, i) => (
            <Button
              key={label}
              onClick={() => setTab(i)}
              sx={{
                borderRadius: 0,
                borderBottom: tab === i ? '2px solid' : '2px solid transparent',
                borderBottomColor: tab === i ? 'primary.main' : 'transparent',
                color: tab === i ? 'primary.main' : 'text.secondary',
                fontWeight: tab === i ? 600 : 400,
                px: 2,
                py: 1,
                minWidth: 'auto',
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      <TabPanel value={tab} index={0}><LeadsTab /></TabPanel>
      <TabPanel value={tab} index={1}><SyncHistoryTab /></TabPanel>
      <TabPanel value={tab} index={2}>
        <SettingsTab
          onSyncNow={handleSyncNow}
          syncLoading={syncLoading}
          syncError={syncError}
          syncSuccess={syncSuccess}
        />
      </TabPanel>
    </Box>
  );
}
